/**
 * OrgMembersPanel — org-admin self-service membership on the Settings page
 * (AW plan D6 / P1.9). Invite a colleague, change roles, remove, resend a
 * pending invite. Account lifecycle (suspend/delete) stays with ChargeStack
 * ops; password recovery is self-service on the sign-in page.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';

const ROLES = ['admin', 'attorney', 'paralegal', 'viewer'];
const ROLE_HELP = {
  admin: 'Manage members and settings; full case access',
  attorney: 'Full case access',
  paralegal: 'View and upload; limited case management',
  viewer: 'Read-only',
};

export default function OrgMembersPanel({ currentUid }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.get('/org/members');
      setMembers(d.members || []);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="settings-group org-members-panel">
      <div className="settings-group-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 className="settings-group-title">Members{!loading && ` (${members.length})`}</h2>
        <button className="btn btn-sm btn-primary" onClick={() => { setShowInvite(v => !v); setNotice(null); }}>
          {showInvite ? 'Cancel' : '+ Invite colleague'}
        </button>
      </div>

      {showInvite && (
        <InviteForm
          onDone={(result) => {
            setShowInvite(false);
            setNotice(result);
            load();
          }}
        />
      )}

      {notice && <InviteNotice result={notice} onDismiss={() => setNotice(null)} />}

      {error && <p className="error-text">{error}</p>}
      {loading ? (
        <p className="loading-text">Loading members…</p>
      ) : (
        <table className="settings-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Role</th><th>Joined</th><th></th></tr>
          </thead>
          <tbody>
            {members.length === 0 && <tr><td colSpan={5} className="empty-row">No members yet</td></tr>}
            {members.map(m => (
              <MemberRow key={m.id || m.user_id} member={m} isSelf={m.user_id === currentUid} onChanged={load} onNotice={setNotice} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function MemberRow({ member, isSelf, onChanged, onNotice }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  async function changeRole(role) {
    if (role === member.role) return;
    setBusy(true); setError(null);
    try {
      await api.patch(`/org/members/${member.id}`, { role });
      onChanged();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  async function remove() {
    setBusy(true); setError(null);
    try {
      await api.del(`/org/members/${member.id}`);
      onChanged();
    } catch (e) { setError(e.message); setBusy(false); setConfirmRemove(false); }
  }

  async function resend() {
    setBusy(true); setError(null);
    try {
      const r = await api.post(`/org/members/${member.id}/resend`);
      onNotice({ kind: 'resend', email: r.email, setup_link: r.setup_link, email_sent: r.email_sent });
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  return (
    <tr>
      <td>{member.display_name || '—'}{isSelf && <span className="badge" style={{ marginLeft: 6 }}>you</span>}</td>
      <td>
        {member.email}
        {member.identity_email && member.identity_email !== member.email && (
          <div className="mono" style={{ fontSize: 11, color: 'var(--text-muted, #6c757d)' }} title="Sign-in username">
            {member.identity_email}
          </div>
        )}
      </td>
      <td>
        <select
          className="form-input form-input-sm"
          value={member.role || ''}
          disabled={busy || !member.id}
          onChange={e => changeRole(e.target.value)}
          title={ROLE_HELP[member.role] || ''}
        >
          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </td>
      <td>{member.created_at ? new Date(member.created_at).toLocaleDateString() : '—'}</td>
      <td>
        <div className="member-actions">
          {member.pending !== false && (
            <button className="btn btn-sm btn-ghost" onClick={resend} disabled={busy || !member.id} title="Send a fresh setup link (pending invites only)">
              Resend invite
            </button>
          )}
          {!isSelf && !confirmRemove && (
            <button className="btn btn-sm btn-ghost" onClick={() => setConfirmRemove(true)} disabled={busy || !member.id}>
              Remove
            </button>
          )}
          {confirmRemove && (
            <>
              <button className="btn btn-sm btn-danger" onClick={remove} disabled={busy}>Confirm remove</button>
              <button className="btn btn-sm btn-ghost" onClick={() => setConfirmRemove(false)} disabled={busy}>Cancel</button>
            </>
          )}
        </div>
        {error && <p className="error-text">{error}</p>}
      </td>
    </tr>
  );
}

function InviteForm({ onDone }) {
  const [form, setForm] = useState({ email: '', display_name: '', role: 'attorney' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      const r = await api.post('/org/members', form);
      onDone({ kind: 'invite', ...r, email: r.member?.email });
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  return (
    <form onSubmit={submit} className="org-invite-form" style={{ display: 'grid', gap: 8, gridTemplateColumns: '2fr 1.5fr 1fr auto', alignItems: 'end', margin: '8px 0 16px' }}>
      <div className="form-group">
        <label className="form-label">Work email *</label>
        <input className="form-input" type="email" required value={form.email}
          onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="colleague@example.com" />
      </div>
      <div className="form-group">
        <label className="form-label">Name *</label>
        <input className="form-input" required value={form.display_name}
          onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} placeholder="First Last" />
      </div>
      <div className="form-group">
        <label className="form-label">Role</label>
        <select className="form-input" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Inviting…' : 'Send invite'}</button>
      {error && <p className="error-text" style={{ gridColumn: '1 / -1' }}>{error}</p>}
      <p className="settings-hint" style={{ gridColumn: '1 / -1', color: 'var(--text-muted, #6c757d)', fontSize: 12, margin: 0 }}>
        {ROLE_HELP[form.role]}. They’ll get an email with a setup link valid for 48 hours.
      </p>
    </form>
  );
}

function InviteNotice({ result, onDismiss }) {
  const [copied, setCopied] = useState(false);
  const link = result.setup_link;
  async function copy() {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* noop */ }
  }
  let headline;
  if (result.kind === 'resend') headline = result.email_sent ? `A fresh setup link was emailed to ${result.email}.` : `Fresh setup link for ${result.email}:`;
  else if (result.already_active) headline = `${result.email} already has an account — they've been added and can sign in now.`;
  else headline = result.email_sent ? `Invite emailed to ${result.email}.` : `Invite created for ${result.email}. Email isn't configured here — send them this link:`;
  return (
    <div className="settings-message success" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <span>{headline}</span>
      {link && !result.email_sent && (
        <>
          <input className="form-input form-input-sm mono" readOnly value={link} onFocus={e => e.target.select()} style={{ flex: '1 1 320px' }} />
          <button type="button" className="btn btn-sm btn-primary" onClick={copy}>{copied ? 'Copied ✓' : 'Copy link'}</button>
        </>
      )}
      <button type="button" className="btn btn-sm btn-ghost" onClick={onDismiss}>Dismiss</button>
    </div>
  );
}
